import React, { useState, useEffect } from "react";
import { useGame } from "../context/GameContext";
import { labelModo } from "../utils/gameUtils";

const AVATARS = [
  { emoji: "🦊", bg: "#ff3e3e22", border: "#ff3e3e", text: "#ff3e3e" },
  { emoji: "👾", bg: "#9b6dff22", border: "#9b6dff", text: "#9b6dff" },
  { emoji: "🦖", bg: "#3effa322", border: "#3effa3", text: "#3effa3" },
  { emoji: "🐱", bg: "#f5c84222", border: "#f5c842", text: "#f5c842" },
  { emoji: "🦁", bg: "#ff6b3e22", border: "#ff6b3e", text: "#ff6b3e" },
  { emoji: "🐧", bg: "#3eaaff22", border: "#3eaaff", text: "#3eaaff" },
  { emoji: "🐷", bg: "#ff3eb522", border: "#ff3eb5", text: "#ff3eb5" },
  { emoji: "🐬", bg: "#3effee22", border: "#3effee", text: "#3effee" },
  { emoji: "👽", bg: "#aaff3e22", border: "#aaff3e", text: "#aaff3e" },
  { emoji: "👻", bg: "#ffffff22", border: "#ffffff", text: "#ffffff" },
];
const BAR_COLORS = ["var(--accent)","var(--purple)","var(--gold)","var(--green)","#ff6b3e","#3eaaff","#ff3eb5","#3effee","#aaff3e","#ffffff"];

// ── Temas por modo ──────────────────────────────────────────
const TEMAS = {
  "perguntas": {
    cor: acertou => acertou ? "var(--green)" : "var(--accent)",
    glow: acertou => acertou ? "rgba(62,255,163,0.4)" : "rgba(255,62,62,0.4)",
    icone: acertou => acertou ? "🕵️" : "😈",
    titulo: acertou => acertou ? "Impostor desmascarado!" : "O impostor escapou!",
    subtitulo: (acertou, nomes) => acertou ? `${nomes} foi eliminado!` : `${nomes} enganou todo mundo!`,
    bgGradient: acertou => acertou
      ? "linear-gradient(135deg, rgba(62,255,163,0.08) 0%, transparent 60%)"
      : "linear-gradient(135deg, rgba(255,62,62,0.08) 0%, transparent 60%)",
  },
  "palavra-secreta": {
    cor: acertou => acertou ? "var(--purple)" : "var(--accent)",
    glow: acertou => acertou ? "rgba(155,109,255,0.4)" : "rgba(255,62,62,0.4)",
    icone: acertou => acertou ? "🔐" : "🤫",
    titulo: acertou => acertou ? "Palavra descoberta!" : "O impostor se safou!",
    subtitulo: (acertou, nomes) => acertou ? `${nomes} era o impostor!` : `${nomes} enganou a todos!`,
    bgGradient: acertou => acertou
      ? "linear-gradient(135deg, rgba(155,109,255,0.08) 0%, transparent 60%)"
      : "linear-gradient(135deg, rgba(255,62,62,0.08) 0%, transparent 60%)",
  },
  "duas-faccoes": {
    cor: acertou => acertou ? "var(--green)" : "var(--accent)",
    glow: acertou => acertou ? "rgba(62,255,163,0.4)" : "rgba(255,62,62,0.4)",
    icone: acertou => acertou ? "⚔️" : "🕵️",
    titulo: acertou => acertou ? "Impostor eliminado!" : "O agente duplo escapou!",
    subtitulo: (acertou, nomes) => acertou ? `${nomes} foi descoberto!` : `${nomes} sobreviveu!`,
    bgGradient: () => "linear-gradient(135deg, rgba(62,170,255,0.06) 0%, rgba(62,255,163,0.06) 100%)",
  },
  "descubra-nota": {
    cor: () => "var(--gold)",
    glow: () => "rgba(245,200,66,0.4)",
    icone: () => "🎯",
    titulo: () => "Jogo encerrado!",
    subtitulo: () => "Veja o placar das duplas abaixo",
    bgGradient: () => "linear-gradient(135deg, rgba(245,200,66,0.08) 0%, transparent 60%)",
  },
  "adivinhe-musica": {
    cor: () => "#3eaaff",
    glow: () => "rgba(62,170,255,0.4)",
    icone: () => "🎵",
    titulo: () => "Fim do show!",
    subtitulo: () => "Confira o placar final",
    bgGradient: () => "linear-gradient(135deg, rgba(62,170,255,0.08) 0%, transparent 60%)",
  },
  "mega-senha": {
    cor: () => "#ff6b3e",
    glow: () => "rgba(255,107,62,0.4)",
    icone: () => "🏆",
    titulo: () => "Partida encerrada!",
    subtitulo: () => "Veja o placar das duplas",
    bgGradient: () => "linear-gradient(135deg, rgba(255,107,62,0.08) 0%, transparent 60%)",
  },
};

// ── Placar de votos (modos com impostor) ─────────────────────
function PlacarVotos({ animado }) {
  const { jogadores, impostoresIdx, resultadoVotos } = useGame();
  const maxVotos = Math.max(...jogadores.map((_, i) => resultadoVotos[i] || 0), 1);

  return (
    <div>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)", marginBottom: 16 }}>
        🗳️ Placar de votos
      </div>
      {jogadores.map((nome, idx) => {
        const votos = resultadoVotos[idx] || 0;
        const pct = maxVotos > 0 ? (votos / maxVotos) * 100 : 0;
        const ehImpostor = impostoresIdx.includes(idx);
        const avatar = AVATARS[idx % AVATARS.length];
        const barCor = BAR_COLORS[idx % BAR_COLORS.length];
        return (
          <div key={idx} className="result-bar-wrap" style={{ animation: `slideUp 0.4s ease ${idx * 100}ms both` }}>
            <div className="result-bar-header">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: avatar.bg, border: `2px solid ${avatar.border}`, color: avatar.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>
                  {avatar.emoji}
                </div>
                <span style={{ fontWeight: 700 }}>{nome}</span>
                {ehImpostor && <span className="badge badge-red" style={{ padding: "2px 7px", fontSize: "0.65rem" }}>IMPOSTOR</span>}
              </div>
              <div style={{ fontFamily: "'Space Mono'", fontWeight: 700, color: votos > 0 ? "var(--text-primary)" : "var(--text-muted)" }}>
                {votos} voto{votos !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="result-bar-track">
              <div className="result-bar-fill" style={{ width: animado ? `${pct}%` : "0%", background: barCor, opacity: votos > 0 ? 1 : 0.3 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Placar de notas ───────────────────────────────────────────
function PlacarNotas() {
  const { notaResultados, jogadores, duplas } = useGame();
  const acertos = notaResultados.filter(r => r.acertou).length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)", marginBottom: 4 }}>
        🎯 Resultado das duplas
      </div>
      {notaResultados.map((r, i) => {
        const avD = AVATARS[r.dador % AVATARS.length];
        const avA = AVATARS[r.adivinhador % AVATARS.length];
        return (
          <div key={i} style={{ background: r.acertou ? "rgba(62,255,163,0.08)" : "rgba(255,62,62,0.08)", border: `1px solid ${r.acertou ? "var(--green)" : "var(--accent)"}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "1.2rem" }}>{avD.emoji}</span>
                <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{jogadores[r.dador]}</span>
                <span style={{ color: "var(--text-muted)" }}>↔️</span>
                <span style={{ fontSize: "1.2rem" }}>{avA.emoji}</span>
                <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{jogadores[r.adivinhador]}</span>
              </div>
              <span style={{ fontSize: "1.4rem" }}>{r.acertou ? "✅" : "❌"}</span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <span style={{ fontSize: "0.78rem", background: "rgba(245,200,66,0.15)", color: "var(--gold)", borderRadius: 8, padding: "3px 10px" }}>🔐 Nota: {r.nota}</span>
              <span style={{ fontSize: "0.78rem", background: r.acertou ? "rgba(62,255,163,0.15)" : "rgba(255,62,62,0.15)", color: r.acertou ? "var(--green)" : "var(--accent)", borderRadius: 8, padding: "3px 10px" }}>🎯 Chute: {r.chute}</span>
            </div>
          </div>
        );
      })}
      <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
        <span style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>Total de acertos</span>
        <span style={{ fontFamily: "'Bebas Neue'", fontSize: "1.6rem", color: "var(--gold)" }}>{acertos}/{notaResultados.length}</span>
      </div>
    </div>
  );
}

// ── Placar de música ──────────────────────────────────────────
function PlacarMusica() {
  const { musicaPlacar, jogadores } = useGame();
  const placarOrdenado = Object.entries(musicaPlacar)
    .map(([idx, pts]) => ({ idx: Number(idx), nome: jogadores[idx], pts }))
    .sort((a, b) => b.pts - a.pts);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)", marginBottom: 4 }}>
        🎵 Placar final
      </div>
      {placarOrdenado.map(({ idx, nome, pts }, pos) => {
        const av = AVATARS[idx % AVATARS.length];
        return (
          <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px", border: `1px solid ${pos === 0 ? "var(--gold)" : "var(--border)"}` }}>
            <div style={{ width: 28, fontSize: "1.2rem", textAlign: "center" }}>{pos === 0 ? "👑" : `${pos + 1}°`}</div>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>{av.emoji}</div>
            <div style={{ flex: 1, fontWeight: 700 }}>{nome}</div>
            <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.6rem", color: pos === 0 ? "var(--gold)" : "#3eaaff" }}>{pts} pt{pts !== 1 ? "s" : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Placar Mega Senha ─────────────────────────────────────────
function PlacarMegaSenha() {
  const { megaSenhaRodadas, megaSenhaPares, jogadores } = useGame();
  // contar pontos por par (adivinhador ganha o ponto)
  const pontos = {};
  megaSenhaRodadas.forEach(r => {
    if (r.vencedor !== null && r.vencedor !== undefined) {
      pontos[r.vencedor] = (pontos[r.vencedor] || 0) + 1;
    }
  });
  const paresOrdenados = [...megaSenhaPares]
    .map((p, i) => ({ parIdx: i, dador: p.dador, adiv: p.adivinhador, pts: pontos[i] || 0 }))
    .sort((a, b) => b.pts - a.pts);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)", marginBottom: 4 }}>
        🏆 Placar de duplas
      </div>
      {paresOrdenados.map(({ parIdx, dador, adiv, pts }, pos) => {
        const avD = AVATARS[dador % AVATARS.length];
        const avA = AVATARS[adiv % AVATARS.length];
        return (
          <div key={parIdx} style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px", border: `1px solid ${pos === 0 ? "var(--gold)" : "var(--border)"}` }}>
            <div style={{ fontSize: "1.2rem", width: 24 }}>{pos === 0 ? "👑" : `${pos + 1}°`}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
              <span style={{ fontSize: "1.2rem" }}>{avD.emoji}</span>
              <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{jogadores[dador]}</span>
              <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>+</span>
              <span style={{ fontSize: "1.2rem" }}>{avA.emoji}</span>
              <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{jogadores[adiv]}</span>
            </div>
            <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.6rem", color: pos === 0 ? "var(--gold)" : "#ff6b3e" }}>{pts} pt{pts !== 1 ? "s" : ""}</div>
          </div>
        );
      })}
      <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "12px 16px" }}>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>📋 Histórico de senhas</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {megaSenhaRodadas.map((r, i) => (
            <span key={i} style={{ background: "#ff6b3e20", border: "1px solid #ff6b3e", color: "#ff6b3e", borderRadius: 20, padding: "4px 12px", fontSize: "0.82rem", fontWeight: 700 }}>
              {r.palavra}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── FinalScreen principal ─────────────────────────────────────
export default function FinalScreen() {
  const {
    jogadores, impostoresIdx, resultadoVotos,
    modoJogo, modoJogoTipo,
    novaRodada, novaRodadaPalavra, reiniciarJogo,
    rodada, perguntaAtual, palavraAtual,
    notaResultados, musicaPlacar, megaSenhaRodadas,
  } = useGame();
  const [animado, setAnimado] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimado(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Modos com impostor e votação
  const modoComImpostor = ["perguntas", "palavra-secreta", "duas-faccoes"].includes(modoJogoTipo);

  const maisVotadoIdx = modoComImpostor ? jogadores.reduce((acc, _, i) =>
    (resultadoVotos[i] || 0) > (resultadoVotos[acc] || 0) ? i : acc, 0) : null;
  const acertou = maisVotadoIdx !== null && impostoresIdx.includes(maisVotadoIdx);
  const nomesImpostores = impostoresIdx.map(i => jogadores[i]).join(" & ");

  const tema = TEMAS[modoJogoTipo] || TEMAS["perguntas"];

  const novaRodadaFn = () => {
    if (modoJogoTipo === "palavra-secreta") novaRodadaPalavra();
    else novaRodada();
  };

  // Conteúdo de revelação específico por modo
  const renderRevelacao = () => {
    if (modoJogoTipo === "perguntas") return (
      <div style={{ display: "grid", gap: 8, marginBottom: 20 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)", marginBottom: 4 }}>📋 As perguntas desta rodada</div>
        <div style={{ background: "var(--bg-elevated)", borderRadius: 10, padding: "12px 16px", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginBottom: 4 }}>👤 Jogadores normais</div>
          <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>"{perguntaAtual?.normal}"</div>
        </div>
        <div style={{ background: "var(--accent-dim)", borderRadius: 10, padding: "12px 16px", border: "1px solid var(--border-accent)" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--accent)", marginBottom: 4 }}>🎭 Impostor(es)</div>
          <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>"{perguntaAtual?.impostor}"</div>
        </div>
      </div>
    );

    if (modoJogoTipo === "palavra-secreta") return (
      <div style={{ display: "grid", gap: 8, marginBottom: 20 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)", marginBottom: 4 }}>🔐 As palavras desta rodada</div>
        <div style={{ background: "rgba(155,109,255,0.1)", borderRadius: 10, padding: "12px 16px", border: "1px solid var(--purple)" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--purple)", marginBottom: 4 }}>👤 Grupo</div>
          <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem", color: "var(--purple)" }}>{palavraAtual?.normal}</div>
        </div>
        <div style={{ background: "var(--accent-dim)", borderRadius: 10, padding: "12px 16px", border: "1px solid var(--border-accent)" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--accent)", marginBottom: 4 }}>🕵️ Impostor recebeu</div>
          <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem", color: "var(--accent)" }}>{palavraAtual?.impostor}</div>
        </div>
      </div>
    );

    return null;
  };

  return (
    <div className="app-wrapper" style={{ paddingTop: 80, paddingBottom: 60 }}>
      <div className="card" style={{ maxWidth: 560 }}>

        {/* ── HEADER TEMÁTICO ── */}
        <div style={{
          background: tema.bgGradient(acertou),
          borderBottom: "1px solid var(--border)",
          padding: "32px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{ fontSize: "5rem", marginBottom: 12, animation: "pulse 2s ease infinite", filter: `drop-shadow(0 0 20px ${tema.glow(acertou)})` }}>
            {tema.icone(acertou)}
          </div>
          <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem", letterSpacing: "0.06em", color: "var(--text-secondary)", marginBottom: 6 }}>
            {tema.titulo(acertou)}
          </div>

          {modoComImpostor ? (
            <>
              <div style={{ fontFamily: "'Bebas Neue'", fontSize: "2.6rem", letterSpacing: "0.04em", color: tema.cor(acertou), textShadow: `0 0 30px ${tema.glow(acertou)}`, marginBottom: 12 }}>
                {nomesImpostores}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                <span className="badge badge-red">🎭 IMPOSTOR</span>
                <span className="badge badge-gray">{labelModo(modoJogo)}</span>
                <span className="badge badge-purple">Rodada {rodada}</span>
              </div>
            </>
          ) : (
            <div style={{ color: "var(--text-secondary)", fontSize: "0.92rem", marginTop: 4 }}>
              {tema.subtitulo(acertou, "")}
            </div>
          )}
        </div>

        {/* ── BODY ── */}
        <div className="card-body">
          {renderRevelacao()}
          <div className="divider" style={{ margin: "0 0 20px" }} />

          {modoComImpostor && <PlacarVotos animado={animado} />}
          {modoJogoTipo === "descubra-nota" && <PlacarNotas />}
          {modoJogoTipo === "adivinhe-musica" && <PlacarMusica />}
          {modoJogoTipo === "mega-senha" && <PlacarMegaSenha />}
        </div>

        {/* ── FOOTER ── */}
        <div className="card-footer" style={{ justifyContent: "space-between" }}>
          <button className="btn btn-secondary" onClick={reiniciarJogo}>🏠 Menu</button>
          {(modoJogoTipo === "perguntas" || modoJogoTipo === "palavra-secreta" || modoJogoTipo === "duas-faccoes") && (
            <button className="btn btn-primary" onClick={novaRodadaFn}>🔄 Nova rodada</button>
          )}
        </div>
      </div>
    </div>
  );
}
